// Where the navbar's partner / chain / hotel selection is kept.
//
// PER TAB, NOT PER BROWSER. It lived in localStorage, which every tab in the profile shares, while the
// app treated it as this tab's state. Two tabs open on different chains therefore had ONE stored
// selection and two different navbars, and whichever tab last touched the picker silently won for
// everything that read the store — including the context `api.js` stamps onto /sync/{uuid} calls so the
// backend can enforce access "for superadmins operating within a specific context". A tab could show
// Demo Chain and list Hilton's projects, which is how this was found.
//
// sessionStorage is scoped to the tab and survives a refresh in it, which is exactly the lifetime the
// selection should have: choosing a chain in one tab must not move another tab that someone is reading.
//
// A NEW TAB IS SEEDED ONCE FROM localStorage — the last selection made anywhere — so opening one does
// not land on "All Partners" every time. After that first read the tab owns its own copy and never
// consults the shared value again, so the seed cannot become a back door to the behaviour above.
// localStorage keeps being written purely as the seed for the NEXT new tab; nothing reads it to decide
// what this tab is looking at.
//
// ONE SOURCE OF TRUTH. Everything that needs the selection — AccountContext, the axios interceptor, the
// navbar, the OHIP simulate modal — goes through here. Components reading React state while the
// interceptor read localStorage is the second half of the same bug: within a single tab the list could
// be filtered by one chain and the request authorised as another.

const KEYS = {
    partner: 'selectedPartnerId',
    account: 'selectedAccountId',
    hotel: 'selectedHotelId',
    owner: 'selectionOwnerSub',
};

// sessionStorage and localStorage both throw in a hardened/private context rather than returning null.
// A selection that cannot be persisted is a degraded experience; an exception here would be a blank app.
const safe = (fn, fallback = null) => { try { return fn(); } catch (e) { return fallback; } };

const SEEDED = 'selectionSeededFromLocal';

// Copy the last-used selection into this tab, once. Called before the first read.
const seedOnce = () => safe(() => {
    if (sessionStorage.getItem(SEEDED)) return;
    sessionStorage.setItem(SEEDED, '1');
    for (const k of Object.values(KEYS)) {
        const v = localStorage.getItem(k);
        // Only fill blanks: a tab that already has a value keeps it, so a reload cannot be overwritten
        // by whatever another tab did in the meantime.
        if (v != null && sessionStorage.getItem(k) == null) sessionStorage.setItem(k, v);
    }
});

const read = (key) => safe(() => { seedOnce(); return sessionStorage.getItem(key); });

const write = (key, value) => safe(() => {
    seedOnce();
    if (value == null || value === '') {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
    } else {
        sessionStorage.setItem(key, String(value));
        localStorage.setItem(key, String(value));   // seed for the next NEW tab only
    }
});

export const selectionStorage = {
    KEYS,

    getPartnerId: () => read(KEYS.partner),
    getAccountId: () => read(KEYS.account),
    getHotelId:   () => read(KEYS.hotel),
    getOwnerSub:  () => read(KEYS.owner),

    setPartnerId: (v) => write(KEYS.partner, v),
    setAccountId: (v) => write(KEYS.account, v),
    setHotelId:   (v) => write(KEYS.hotel, v),
    setOwnerSub:  (v) => write(KEYS.owner, v),

    /** The three ids as the API expects them, most specific first — the shape every caller wants. */
    getContext: () => ({
        hotelId:   read(KEYS.hotel),
        accountId: read(KEYS.account),
        partnerId: read(KEYS.partner),
    }),

    /** A different user signed in: drop everything, in this tab AND the seed. */
    clear: () => {
        for (const k of [KEYS.partner, KEYS.account, KEYS.hotel]) write(k, null);
    },
};

export default selectionStorage;
